/**
 * @fileoverview Compact badge + popover to inspect linked payments for an invoice.
 * @module features/invoices/components/InvoicePaymentsBadge
 */

import { type ReactElement, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { Building2, Loader2, Undo2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Badge, Money } from '@/shared/components/ui'
import { cn } from '@/lib/utils'
import {
  getInvoiceLinks,
  unlinkPaymentFromInvoice,
  updateInvoice,
} from '@/features/invoices/api/invoicesService'
import type { SettlementType } from '@/features/invoices/schemas/invoicesSchema'

interface InvoicePaymentsBadgeProps {
  invoiceUuid: string
  linkedPaymentsCount?: number
  settlementType?: SettlementType | null | undefined
  className?: string
}

export default function InvoicePaymentsBadge({
  invoiceUuid,
  linkedPaymentsCount,
  settlementType,
  className,
}: InvoicePaymentsBadgeProps): ReactElement {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [prefetch, setPrefetch] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invoices', 'links', invoiceUuid],
    queryFn: () => getInvoiceLinks(invoiceUuid),
    enabled: open || prefetch,
    staleTime: 5 * 60 * 1000,
  })

  const payments = data?.payments ?? []
  const linkedCount = data?.payments.length ?? linkedPaymentsCount ?? 0
  const hasLinkedPayments = linkedCount > 0
  const isPlatformDeducted = settlementType === 'PLATFORM_DEDUCTED'

  const unlinkMutation = useMutation({
    mutationFn: async (paymentUuid: string) => unlinkPaymentFromInvoice(invoiceUuid, paymentUuid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices', 'links', invoiceUuid] })
      void queryClient.invalidateQueries({ queryKey: ['invoices'] })
      void queryClient.invalidateQueries({ queryKey: ['payments'] })
      void queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : 'Failed to unlink payment')
    },
  })

  const settlementMutation = useMutation({
    mutationFn: async (type: SettlementType | null) =>
      updateInvoice(invoiceUuid, type ? { settlementType: type } : { clearSettlementType: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] })
      void queryClient.invalidateQueries({ queryKey: ['invoices', invoiceUuid] })
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update settlement type')
    },
  })

  const badgeLabel = useMemo(() => 'Payments', [])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
          onMouseEnter={() => setPrefetch(true)}
          onPointerDown={event => event.stopPropagation()}
          onClick={event => event.stopPropagation()}
          aria-label="Linked payments"
        >
          <Badge
            variant="secondary"
            className={cn(
              'h-7 gap-1 px-2 text-[11px] text-muted-foreground/80 transition-colors hover:bg-muted-foreground/20',
              (hasLinkedPayments || isPlatformDeducted) && 'text-foreground/85',
              className,
            )}
          >
            {badgeLabel}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-3" align="end" sideOffset={4}>
        <div className="space-y-2">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              <span>Loading...</span>
            </div>
          ) : isError ? (
            <p className="text-sm text-destructive">Failed to load linked payments</p>
          ) : hasLinkedPayments ? (
            <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
              {payments.map(payment => {
                const primaryLabel =
                  payment.counterpartyName?.trim() || payment.title?.trim() || payment.paymentUuid
                const dateLabel = payment.txnDate ? format(new Date(payment.txnDate), 'dd MMM') : null

                return (
                  <div key={payment.paymentUuid} className="rounded-md border border-border/60 p-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium" title={primaryLabel}>
                          {primaryLabel}
                        </p>
                        <p className="truncate text-xs text-muted-foreground/75" title={payment.title ?? ''}>
                          {dateLabel ? `${dateLabel} | ` : ''}
                          {payment.title ?? 'Payment'}
                        </p>
                      </div>
                      {typeof payment.reportingAmount === 'number' && payment.reportingCurrency ? (
                        <span className="shrink-0 text-xs text-foreground/80">
                          <Money
                            value={payment.reportingAmount}
                            currency={payment.reportingCurrency}
                            minFraction={2}
                            maxFraction={2}
                          />
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-2 flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        disabled={unlinkMutation.isPending}
                        onPointerDown={event => event.stopPropagation()}
                        onClick={event => {
                          event.stopPropagation()
                          unlinkMutation.mutate(payment.paymentUuid)
                        }}
                      >
                        {unlinkMutation.isPending ? 'Unlinking...' : 'Unlink'}
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : isPlatformDeducted ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-2.5">
                <Building2 className="size-4 shrink-0 text-emerald-400" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground/90">Platform-deducted</p>
                  <p className="text-xs text-muted-foreground/75">Settled by platform from payout</p>
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="w-full text-muted-foreground"
                disabled={settlementMutation.isPending}
                onPointerDown={event => event.stopPropagation()}
                onClick={event => {
                  event.stopPropagation()
                  settlementMutation.mutate(null)
                }}
              >
                <Undo2 className="mr-1.5 size-3.5" />
                {settlementMutation.isPending ? 'Clearing...' : 'Clear status'}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">No linked payments</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full"
                disabled={settlementMutation.isPending}
                onPointerDown={event => event.stopPropagation()}
                onClick={event => {
                  event.stopPropagation()
                  settlementMutation.mutate('PLATFORM_DEDUCTED')
                }}
              >
                <Building2 className="mr-1.5 size-3.5" />
                {settlementMutation.isPending ? 'Saving...' : 'Mark as platform-deducted'}
              </Button>
              <p className="text-[11px] leading-tight text-muted-foreground/60">
                Settled by platform (deducted from payout)
              </p>
            </div>
          )}

        </div>
      </PopoverContent>
    </Popover>
  )
}
